/*
  Softy product page
  ---------------------------------------------------------------
  Two small pieces, both optional:

  1. <pack-picker> — the 6 / 12 / 24 pills. In "quantity" mode the
     product has no pack variants yet, so a pill means "this many of
     the base pack": it rewrites the price, tells the shopper how the
     order is made up, and multiplies the quantity on its way into
     the cart. In "siblings" mode the pills are plain links and this
     does nothing.

  2. The mobile gallery counter, which follows the swipe.

  3. The sticky buy panel's height, published as --sh-info-h so the
     CSS can let a panel taller than the screen scroll with the page
     until its bottom shows, then hold.
*/

if (!customElements.get('pack-picker')) {
  customElements.define(
    'pack-picker',
    class PackPicker extends HTMLElement {
      connectedCallback() {
        if (this.dataset.mode !== 'quantity') return;

        this.pills = Array.from(this.querySelectorAll('button.sh-pack__pill'));
        if (!this.pills.length) return;

        this.base = parseInt(this.dataset.base, 10) || 1;
        this.note = this.querySelector('[data-sh-pack-note]');
        this.units = 1;

        this.pills.forEach((pill) => pill.addEventListener('click', () => this.select(pill)));
        this.addEventListener('keydown', this.onKeydown.bind(this));

        // Dawn reads the form synchronously in its own submit handler,
        // so the multiplied quantity has to be in place before that:
        // capture on the document runs first, and the displayed value
        // goes back to packs once the handler has read it.
        this.onSubmit = this.onSubmit.bind(this);
        document.addEventListener('submit', this.onSubmit, true);

        // Dawn's quantity input only exists when the template has a
        // quantity_selector block. Without it the picker owns a hidden
        // quantity field on the form, so the multiplier always reaches
        // the cart.
        if (!this.quantityInput) {
          const form = document.getElementById(this.dataset.form);
          if (form && !form.querySelector('input[name="quantity"]')) {
            this.ownInput = document.createElement('input');
            this.ownInput.type = 'hidden';
            this.ownInput.name = 'quantity';
            this.ownInput.value = '1';
            form.appendChild(this.ownInput);
          }
        }

        const selected = this.pills.find((pill) => pill.classList.contains('is-selected'));
        if (selected) this.select(selected, { silent: true });
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmit, true);
      }

      get quantityInput() {
        return document.getElementById(`Quantity-${this.dataset.section}`);
      }

      select(pill, { silent = false } = {}) {
        this.pills.forEach((other) => {
          const on = other === pill;
          other.classList.toggle('is-selected', on);
          other.setAttribute('aria-checked', on ? 'true' : 'false');
        });

        this.units = parseInt(pill.dataset.units, 10) || 1;
        const cans = pill.dataset.cans;
        if (this.ownInput) this.ownInput.value = String(this.units);

        if (this.note) {
          this.note.textContent =
            this.units > 1 ? `${cans} cans · ${this.units} × ${this.base}-can packs` : `${cans} cans`;
        }

        this.updatePrice(pill);
        if (!silent) pill.focus();
      }

      updatePrice(pill) {
        const price = document.getElementById(`price-${this.dataset.section}`);
        if (!price || !pill.dataset.price) return;

        price.querySelectorAll('.price-item--regular, .price-item--sale').forEach((item) => {
          // The compare-at price sits inside an <s> of its own.
          const struck = item.tagName === 'S' || item.closest('s');
          const value = struck ? pill.dataset.compare : pill.dataset.price;
          if (value) item.textContent = value;
        });
      }

      onKeydown(event) {
        const index = this.pills.indexOf(document.activeElement);
        if (index === -1) return;

        let next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = index + 1;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = index - 1;
        if (next === null) return;

        event.preventDefault();
        this.select(this.pills[(next + this.pills.length) % this.pills.length]);
      }

      onSubmit(event) {
        if (this.ownInput || this.units <= 1) return;
        const form = event.target;
        // getAttribute, not .id: the form holds an input named "id"
        // (the variant), and that shadows the property.
        if (!form || form.getAttribute('id') !== this.dataset.form) return;

        const input = this.quantityInput;
        if (!input) return;

        const packs = parseInt(input.value, 10) || 1;
        input.value = packs * this.units;
        setTimeout(() => {
          input.value = packs;
        });
      }
    }
  );
}

document.querySelectorAll('.sh-pdp-gallery').forEach((gallery) => {
  const counter = gallery.querySelector('[data-sh-gallery-current]');
  const items = Array.from(gallery.querySelectorAll('.sh-pdp-gallery__item'));
  if (!counter || items.length < 2) return;

  const list = gallery.querySelector('.sh-pdp-gallery__list');
  const segments = Array.from(gallery.querySelectorAll('.sh-pdp-gallery__segment'));
  const observer = new IntersectionObserver(
    (entries) => {
      // Only a scroller on mobile; on desktop every item is on screen.
      if (list.scrollWidth <= list.clientWidth) return;
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const index = items.indexOf(entry.target);
        counter.textContent = index + 1;
        segments.forEach((segment, i) => segment.classList.toggle('is-current', i === index));
      });
    },
    { root: list, threshold: 0.6 }
  );
  items.forEach((item) => observer.observe(item));
});

/*
  Phone buy bar: mirrors the real button (label, disabled state) and
  the price on the panel, shows while the real button is off screen,
  and presses it on tap.
*/
document.querySelectorAll('[data-sh-buy-bar]').forEach((bar) => {
  const target = document.getElementById(bar.dataset.shBuyBarFor);
  const button = bar.querySelector('[data-sh-buy-bar-button]');
  if (!target || !button) return;

  const price = bar.querySelector('[data-sh-buy-bar-price]');
  const panel = target.closest('product-info') || document;

  const mirror = () => {
    const label = target.querySelector('span');
    if (label && label.textContent.trim()) button.textContent = label.textContent.trim();
    button.disabled = target.disabled || target.getAttribute('aria-disabled') === 'true';
    const shown = panel.querySelector('.price__regular .price-item--regular, .price-item--regular');
    if (price && shown && shown.textContent.trim()) price.textContent = shown.textContent.trim();
  };

  const show = (on) => {
    bar.classList.toggle('is-visible', on);
    bar.setAttribute('aria-hidden', String(!on));
    button.tabIndex = on ? 0 : -1;
    document.documentElement.classList.toggle('sh-buy-bar-open', on);
    if (on) mirror();
  };

  new IntersectionObserver(
    (entries) => entries.forEach((entry) => show(!entry.isIntersecting)),
    { threshold: 0 }
  ).observe(target);

  button.addEventListener('click', () => target.click());
  panel.addEventListener('change', mirror);
  panel.addEventListener('click', () => setTimeout(mirror, 60));
});

document.querySelectorAll('.product--sh-sticky .product__info-container').forEach((panel) => {
  const publish = () => panel.style.setProperty('--sh-info-h', `${panel.offsetHeight}px`);
  publish();
  // Accordions opening and late fonts both change the height.
  if ('ResizeObserver' in window) new ResizeObserver(publish).observe(panel);
});
